import { useRouter } from "next/router";

import { FC, useEffect, useMemo } from "react";

import endsWith from "lodash/endsWith";

import { isPast, isFuture } from "date-fns";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import useCart from "modules/Cart/useCart";

import Counter from "components/Counter";
import Card from "components/Card";
import Button from "components/CustomButton";
import InfoBox from "components/InfoBox";
import { INFO_BOX_TYPE } from "components/InfoBox/types";
import CustomLink from "components/CustomLink";
import BoxLabel from "components/BoxLabel";
import { COLOR } from "components/BoxLabel/config";
import CustomTooltip from "components/CustomTooltip";

import { PURPOSES_TYPES } from "enums/purposesTypes";
import { CART_ITEM_TYPES } from "enums/cartItemTypes";

import { claimingEnterForm } from "config/ga";
import { APP_ROUTES } from "config/routes";

import { formatUTCDate } from "utils/date";
import { logGAEvent } from "utils/ga";
import {
  isAvailableRedeem,
  isRedeemed,
  isWillBeRedeemAvailable,
} from "utils/redeem";
import { scrollToTarget } from "utils/document";

import { RedeemProposalStatus } from "services/redeem/RedeemProposalResult";

const PHYSICAL_REDEEM = [
  PURPOSES_TYPES.PHYSICAL,
  PURPOSES_TYPES.PHYSICAL_TO_NFT,
];

interface RedeemInfoProps {
  contractAddress: string;
  tokenId: number;
  isOwner?: boolean;
  redeemInfo: RedeemProposalStatus[] | null;
  redeemLimitDate?: Date | number | null;
}

const PhysicalRedeem: FC<RedeemInfoProps> = ({
  contractAddress,
  tokenId,
  isOwner,
  redeemInfo,
  redeemLimitDate,
}) => {
  const { addOrderItemToCart, isItemInTheCart } = useCart();
  const theme = useTheme();
  const { asPath } = useRouter();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (endsWith(asPath, "#redeem")) {
      scrollToTarget("#redeem");
    }
  }, [asPath]);

  const isPurposeRedeemed = PHYSICAL_REDEEM.some((purpose) =>
    isRedeemed(redeemInfo, purpose),
  );
  const isPurposeAvailable = PHYSICAL_REDEEM.some((purpose) =>
    isAvailableRedeem(redeemInfo, purpose),
  );
  const isWillBeAvailable = PHYSICAL_REDEEM.some((purpose) =>
    isWillBeRedeemAvailable(redeemInfo, purpose),
  );

  const isPhysicalRedeemDateReached = useMemo(() => {
    if (!redeemInfo || !redeemLimitDate) {
      return false;
    }

    return redeemLimitDate && isPast(new Date(redeemLimitDate));
  }, [redeemInfo, redeemLimitDate]);

  const isItemAddedToCart = isItemInTheCart({
    type: CART_ITEM_TYPES.REDEEM,
    contractAddress,
    tokenId,
  });

  const handleAddToCart = () => {
    addOrderItemToCart(contractAddress, tokenId);
  };

  const handleOrder = () => {
    logGAEvent(claimingEnterForm);
  };

  return (
    <Card bg="plain">
      <Box id="redeem" sx={{ scrollMarginTop: "100px" }} />
      <Typography component="h4" variant="h4" gutterBottom>
        REDEEM
      </Typography>
      <Typography mb={1}>
        Redeem your item to the form of physical product.
        <CustomTooltip>
          <div>
            <Typography variant="body2" color="textSecondary">
              - Physical form - here we materialize your NFT to the form of a
              physical item. Unique product is being manufactured and delivered
              right to your chosen location.
            </Typography>
          </div>
        </CustomTooltip>
      </Typography>
      {!isOwner && (
        <InfoBox
          type={INFO_BOX_TYPE.INFO}
          sx={{ display: { xs: "none", lg: "block" } }}
        >
          You are currently not the owner of this item. Buy some NFTs from our
          great Partners in{" "}
          <CustomLink href={APP_ROUTES.STORES}>Uniqly Stores</CustomLink>!
        </InfoBox>
      )}
      {isOwner && (
        <>
          {!isPurposeRedeemed && isPurposeAvailable && (
            <Box mb={1}>
              <BoxLabel
                color={COLOR.SECONDARY}
                show={Boolean(
                  redeemLimitDate &&
                    isFuture(new Date(redeemLimitDate)) &&
                    PHYSICAL_REDEEM.includes(PURPOSES_TYPES.PHYSICAL),
                )}
                boxSx={{
                  width: "max-content",
                  px: 2,
                  py: 0.5,
                }}
              >
                {redeemLimitDate ? (
                  <Typography variant="body2" fontWeight={600}>
                    <Counter isPlain time={new Date(redeemLimitDate)} /> left
                  </Typography>
                ) : null}
              </BoxLabel>
            </Box>
          )}
          {!isPurposeRedeemed && !isPurposeAvailable && (
            <Box mb={1}>
              <BoxLabel color={COLOR.SECONDARY} padding={0.5}>
                <Typography variant="body2" fontWeight={600}>
                  {isWillBeAvailable ? "Available soon" : "Not available"}
                </Typography>
              </BoxLabel>
            </Box>
          )}
          {isPhysicalRedeemDateReached && redeemLimitDate && (
            <InfoBox type={INFO_BOX_TYPE.INFO} sx={{ mb: 2 }}>
              Redeem is no longer available for this item, as the Store Owner
              set the redeem time limit to{" "}
              {formatUTCDate(new Date(redeemLimitDate))}
            </InfoBox>
          )}
          <Box>
            <Button
              sx={{ mr: { xs: 0, sm: 1 }, mb: { xs: 1, sm: 0 } }}
              href={`${APP_ROUTES.REDEEM}/${contractAddress}/${tokenId}/${PURPOSES_TYPES.PHYSICAL}`}
              minWidth={!isMobile}
              fullWidth={isMobile}
              disabled={
                !(isPurposeAvailable && isWillBeAvailable) || isItemAddedToCart
              }
              onClick={handleOrder}
            >
              Order
            </Button>
            <Button
              minWidth={!isMobile}
              fullWidth={isMobile}
              disabled={
                !(isPurposeAvailable && isWillBeAvailable) || isItemAddedToCart
              }
              onClick={handleAddToCart}
            >
              Add to cart
            </Button>
          </Box>
          {isItemAddedToCart && (
            <InfoBox type={INFO_BOX_TYPE.SUCCESS} sx={{ mt: 2 }}>
              item has already been added to cart
            </InfoBox>
          )}
        </>
      )}
    </Card>
  );
};

export default PhysicalRedeem;
